import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import BrandSelect from '../ui/BrandSelect';
import { Button } from '../ui/button';
import { Actions as CONN_ACTIONS } from '../../store/actions/connectionActions';
import { mappingRowSelect } from '../utils/ReactSelectStyles';
import ConnectionStatus from './ConnectionStatus';

const AuthPanel = ({
  connectorId,
  descriptor,
  externalAccounts,
  loading,
  startConnectionConnect,
  disconnectConnection,
  getExternalAccounts,
  selectExternalAccount,
}) => {
  const status = descriptor?.status;
  const isConnected = status === 'connected' || status === 'needs_organisation';
  const strategy = descriptor?.auth?.strategy || 'oauth';
  const optionsUrl = `/my/connections/${connectorId}/external-accounts`;
  const submitUrl = optionsUrl;

  useEffect(() => {
    if (!connectorId || status !== 'connected') return;
    getExternalAccounts(connectorId, optionsUrl);
  }, [connectorId, status, getExternalAccounts]);

  const options = useMemo(
    () =>
      (externalAccounts?.accounts || []).map((a) => ({
        value: a.id,
        label: a.label,
        account: a,
      })),
    [externalAccounts],
  );

  const selected =
    options.find((o) => o.value === externalAccounts?.selectedId) ||
    options.find((o) => o.label === descriptor?.externalTenantName) ||
    null;

  const organisationName =
    selected?.label || descriptor?.externalTenantName || descriptor?.externalOrganisationName || null;

  if (strategy !== 'oauth') {
    return (
      <p className='text-sm text-muted-foreground'>
        This connector uses an auth strategy that is not yet supported in the UI ({strategy}).
      </p>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
        <ConnectionStatus
          status={status}
          lastError={descriptor?.lastError}
          organisationName={status === 'connected' ? organisationName : null}
        />
        <div className='flex flex-wrap gap-2'>
          {!isConnected && (
            <Button loading={loading?.connect} onClick={() => startConnectionConnect(connectorId)}>
              Connect
            </Button>
          )}
          {isConnected && (
            <>
              <Button
                variant='outline'
                loading={loading?.connect}
                onClick={() => startConnectionConnect(connectorId, { reconnect: true })}>
                Reconnect
              </Button>
              <Button
                variant='destructive'
                loading={loading?.disconnect}
                onClick={() => disconnectConnection(connectorId)}>
                Disconnect
              </Button>
            </>
          )}
        </div>
      </div>

      {status === 'connected' && (
        <div className='flex flex-col gap-3 sm:flex-row sm:items-end'>
          <div className='w-full max-w-md min-w-0'>
            <label className='mb-1 block text-sm font-medium text-foreground'>Organisation</label>
            <BrandSelect
              styles={mappingRowSelect}
              options={options}
              value={selected}
              isLoading={loading?.externalAccounts}
              placeholder='Select organisation'
              onChange={(opt) => {
                if (opt?.account) selectExternalAccount(connectorId, opt.account, submitUrl);
              }}
            />
          </div>
          <Button
            variant='outline'
            loading={loading?.externalAccounts}
            onClick={() => getExternalAccounts(connectorId, optionsUrl)}>
            Refresh
          </Button>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state.connections?.loading,
  externalAccounts: state.connections?.externalAccounts,
});

const mapDispatchToProps = (dispatch) => ({
  startConnectionConnect: (id, opts) => dispatch(CONN_ACTIONS.startConnectionConnect(id, opts)),
  disconnectConnection: (id) => dispatch(CONN_ACTIONS.disconnectConnection(id)),
  getExternalAccounts: (id, url) => dispatch(CONN_ACTIONS.getExternalAccounts(id, url)),
  selectExternalAccount: (id, account, url) => dispatch(CONN_ACTIONS.selectExternalAccount(id, account, url)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthPanel);
